import {
    type FC
} from "react";
import {
    View
} from "react-native";
import type IStatTileDeltaProps from "./type";
import stylesheet, {
    STAT_TILE_DELTA_FRACTION_DIGITS,
    getStatTileDeltaDirection,
    getStatTileDeltaType,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useStatTileContext
} from "../../context";
import {
    TrendingDown as TrendingDownIcon,
    TrendingUp as TrendingUpIcon,
    Minus as MinusIcon
} from "lucide-react-native";
import {
    formatPercent,
    formatNumber
} from "../../../../helpers/locale";
import Skeleton from "../../../skeleton";
import Badge from "../../../badge";
import Text from "../../../text";

const STAT_TILE_DELTA_LABELS = {
    unchanged: "stat-tile-unchanged",
    decrease: "stat-tile-decrease",
    increase: "stat-tile-increase"
} as const;

const STAT_TILE_DELTA_ICONS = {
    decrease: TrendingDownIcon,
    increase: TrendingUpIcon,
    unchanged: MinusIcon
};

const StatTileDelta: FC<IStatTileDeltaProps> = ({
    customLocalize: customLocalizeProp,
    customTheme: customThemeProp,
    isIncreaseGood = true,
    unit = "percent",
    description,
    style,
    value,
    ...props
}) => {
    const context = useStatTileContext({
        part: "delta"
    });

    const customTheme = customThemeProp ?? context?.customTheme;

    const {
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalizeProp ?? context?.customLocalize);

    const {
        container: containerDynamicStyle
    } = useStyles({
        spaces
    });

    if(!context) {
        return null;
    }

    if(context.isLoading) {
        return <Skeleton
            typography="labelSmallSize"
            customTheme={customTheme}
            variant="text"
            width="40%"
        />;
    }

    const direction = getStatTileDeltaDirection({
        value
    });

    const DirectionIcon = STAT_TILE_DELTA_ICONS[direction];

    const signOptions = {
        maximumFractionDigits: STAT_TILE_DELTA_FRACTION_DIGITS,
        minimumFractionDigits: STAT_TILE_DELTA_FRACTION_DIGITS,
        signDisplay: "exceptZero"
    } as const;

    const rawText = unit === "percent" ? formatPercent({
        locale: activeLocale,
        options: signOptions,
        value
    }) : localize("stat-tile-points", [
        formatNumber({
            locale: activeLocale,
            options: signOptions,
            value
        })
    ]);

    const text = rawText.replace("-", "−");

    return <View
        {...props}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
    >
        <Badge
            accessibilityLabel={`${localize(STAT_TILE_DELTA_LABELS[direction])}: ${text}`}
            titleStyle={stylesheet.badge}
            type={getStatTileDeltaType({
                isIncreaseGood,
                value
            })}
            customTheme={customTheme}
            icon={({
                customColor,
                size
            }) => <DirectionIcon
                color={customColor}
                size={size}
            />}
            iconPosition="start"
            variant="tonal"
            size="small"
            title={text}
            role="img"
        />
        {
            description ?
                <Text
                    style={stylesheet.description}
                    customTheme={customTheme}
                    variant="bodySmallSize"
                    color="low"
                >
                    {description}
                </Text>
            :
                null
        }
    </View>;
};
export default StatTileDelta;
